import { distinctUntilChanged, fromEvent, Observable, Subscription } from 'rxjs';
import { fabric } from 'fabric';
import { filter, map } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { Injector, NgZone } from '@angular/core';
import { ScribbleType } from 'src/app/services/session-shared-data.service';
import { filterNil } from '@ngneat/elf';
import { defaults, flatten } from 'lodash';
import { BoardItemsRepository } from 'src/app/state/board-items-repository.interface';
import { isScreenShareAnnotationsSpaceID } from 'src/app/sessions/common/session-call-user/screen-share-annotations/screen-share-annotations.utils';
import { getFabricObjectWithAbsolutePosition, YCanvas } from '../../wb-canvas/fabric-utils';
import { SessionsSyncService } from '../../../../services/sessions-sync.service';
import { SessionSharedDataService } from '../../../../services/session-shared-data.service';
import { SpaceBoardsService } from '../../../../services/space-boards.service';
import { YSessionBoardItemsAdapter } from '../../../common/y-session-board-items-adapter.service';
import { ObservableUtils } from '../../../../utilities/ObservableUtils';
import { FLAGS, FlagsService } from '../../../../services/flags.service';
import { MainToolbarService } from '../../wb-main-toolbar-v2/services/main-toolbar.service';
import { UserService } from '../../../../services/user.service';
import { isBoardItem } from '../../../common/y-session';
import { ScreenShareAnnotationsManagerService } from '../../services/screen-share-annotations-manager.service';
import { BoardItemsFactory } from './board-items-factory';
import { BoardItem } from './board.item';
import { CreationContext } from './board-item-configurations';
import {
  ItemModelToBoardItemsMap,
  ItemModelToConfigurationsMap,
} from './board-item-configurations-factory';
import { BoardItemsInBoundUpdatesManagerService } from './board-items-in-bound-updates-manager.service';
import {
  isValidCanvasDimensions,
  normalizeObjectCoordinates,
} from './board-items-normalized-coordinates.utils';

/**
 * Properties added to Fabric.js objects to track board-specific metadata and state.
 * Used for synchronization and event handling between local and remote updates.
 */
export interface FabricExtraProperties {
  // Used to determine the version of the object to check if it was modified or not
  vuid?: string;
  // universal identifier for the object
  uid?: string;
  // The user that created/owns the object
  userId?: string;

  // We are always listening to the object:added and object:removed events from fabric,
  // And when such events happen we send the update to the server
  // But When an object was added/deleted as a response to a remote update, still the events mentioned above will be triggered
  // And we want to skip them as the updates are already in the server
  skipRemoteAddition?: boolean;
  skipRemoteDeletion?: boolean;

  ScribbleType: ScribbleType;
  boardUid: string;

  // used for fabric objects that have content, .i.e items that can be edited other than resizing, moving, or rotating of the whole object
  isEditing?: boolean;
}

export type FabricObject = ((fabric.Object & FabricExtraProperties) | BoardItem) & {
  binaryBuffer?: Uint8Array;
  order?: number;
  locked?: boolean;
};

/**
 * Core manager class for handling board items in the canvas.
 * Responsible for:
 * - Synchronizing local and remote board items
 * - Managing item lifecycle (creation, modification, deletion)
 * - Handling Fabric.js events and updates
 * - Coordinating with Yjs for real-time collaboration
 *
 * Key features:
 * - Singleton pattern for global access
 * - Event subscription management
 * - Bidirectional sync with remote updates
 * - Item state management and persistence
 */
export interface BoardItemsManagerOptions {
  subscribeToActiveBoard?: boolean;
  setAsGlobalInstance?: boolean;
}

export class BoardItemsManager {
  static instance?: BoardItemsManager;

  private canvas: YCanvas;
  private spaceId: string;
  private boardId: string;

  private currentBoardSubscription = new Subscription();
  private remoteUpdateToCanvasSubscription = new Subscription();
  private selectionCreatedSubscription = new Subscription();
  private selectionUpdatedSubscription = new Subscription();
  private objectModifiedSubscription = new Subscription();
  private objectAddedSubscription = new Subscription();
  private objectRemovedSubscription = new Subscription();

  private zone: NgZone;
  private sharedDataService: SessionSharedDataService;
  private sessionSyncService: SessionsSyncService;
  private ySessionBoardItemsAdapter: YSessionBoardItemsAdapter;
  private boardItemsFactory: BoardItemsFactory;
  private spaceBoardsService: SpaceBoardsService;
  private inBoundUpdatesService?: BoardItemsInBoundUpdatesManagerService;
  private observerUtils: ObservableUtils;
  private flagsService: FlagsService;
  private mainToolbarService: MainToolbarService;
  private userService: UserService;
  private screenShareAnnotationsManagerService: ScreenShareAnnotationsManagerService;
  constructor(
    canvas: YCanvas,
    spaceId: string,
    boardId: string,
    private injector: Injector,
    private readonly options: BoardItemsManagerOptions = {},
    private boardItemsRepository?: BoardItemsRepository,
  ) {
    this.zone = injector.get<NgZone>(NgZone);
    this.sharedDataService = injector.get<SessionSharedDataService>(SessionSharedDataService);
    this.sessionSyncService = injector.get<SessionsSyncService>(SessionsSyncService);
    this.ySessionBoardItemsAdapter =
      injector.get<YSessionBoardItemsAdapter>(YSessionBoardItemsAdapter);
    this.boardItemsFactory = injector.get<BoardItemsFactory>(BoardItemsFactory);
    this.spaceBoardsService = injector.get<SpaceBoardsService>(SpaceBoardsService);
    this.observerUtils = injector.get<ObservableUtils>(ObservableUtils);
    this.flagsService = injector.get<FlagsService>(FlagsService);
    this.mainToolbarService = injector.get<MainToolbarService>(MainToolbarService);
    this.userService = injector.get<UserService>(UserService);
    this.screenShareAnnotationsManagerService = injector.get<ScreenShareAnnotationsManagerService>(
      ScreenShareAnnotationsManagerService,
    );

    this.canvas = canvas;
    this.spaceId = spaceId;
    this.boardId = boardId;

    this.setUpSubscriptionToRemoteBoardItems();
    this.setUpSubscriptionToLocalBoardItems();

    // set as global instance only if not explicitly disabled
    if (this.options.setAsGlobalInstance !== false) {
      BoardItemsManager.instance = this;
    }
  }

  private setUpSubscriptionToRemoteBoardItems() {
    this.inBoundUpdatesService = new BoardItemsInBoundUpdatesManagerService(
      this.canvas,
      this.spaceId,
      this.boardId,
      this.injector,
    );

    const shouldSubscribeToActiveBoard = this.options.subscribeToActiveBoard !== false;
    if (shouldSubscribeToActiveBoard) {
      this.currentBoardSubscription.unsubscribe();
      this.currentBoardSubscription = this.spaceBoardsService.activeSpaceSelectedBoard$
        .pipe(
          map((selectedBoard) => selectedBoard?.uid),
          filterNil(),
          distinctUntilChanged(),
        )
        .subscribe((selectedBoardUid) => {
          this.boardId = selectedBoardUid;
          this.inBoundUpdatesService?.onBoardChange(selectedBoardUid);
        });
      return;
    }

    this.inBoundUpdatesService.onBoardChange(this.boardId, this.boardItemsRepository);
  }

  private setUpSubscriptionToLocalBoardItems(): void {
    this.zone.runOutsideAngular(() => {
      this.setUpSendObjectsModificationsUpdatesSubscription();
      this.setUpSendObjectsAdditionsUpdatesSubscription();
      this.setUpSendObjectsRemovalUpdatesSubscription();
    });
  }

  private setUpSendObjectsRemovalUpdatesSubscription() {
    const removedObjects$ = fromEvent(this.canvas, 'object:removed').pipe(
      filter((event) => !event.target?.excludeFromExport),
      map((event) => this.getObjectFromEvent(event)),
      filter((o) => !o.skipRemoteDeletion),
    );

    this.objectRemovedSubscription.unsubscribe();
    this.objectRemovedSubscription = this.wrapObservableWithBufferIfNeeded(
      removedObjects$,
    ).subscribe(this.sendObjectsRemovalUpdates.bind(this));
  }

  private setUpSendObjectsAdditionsUpdatesSubscription() {
    const addedObjects$ = fromEvent(this.canvas, 'object:added').pipe(
      filter((event) => !event.target?.excludeFromExport),
      map((event) => this.getObjectFromEvent(event)),
      filter((object) => !object.skipRemoteAddition),
    );

    this.objectAddedSubscription.unsubscribe();
    this.objectAddedSubscription = this.wrapObservableWithBufferIfNeeded(addedObjects$).subscribe(
      this.sendObjectsAdditionUpdates.bind(this),
    );
  }

  private setUpSendObjectsModificationsUpdatesSubscription() {
    const modifiedObjects$ = fromEvent(this.canvas, 'object:modified').pipe(
      map((event) => this.getObjectsFromModificationEvent(event)),
    );

    this.objectModifiedSubscription.unsubscribe();
    this.objectModifiedSubscription = this.wrapArrayObservableWithBufferIfNeeded(
      modifiedObjects$,
    ).subscribe((fabricObjects) => this.sendObjectsModificationsUpdate(flatten(fabricObjects)));
  }

  private sendObjectsModificationsUpdate(fabricObjectsUpdates: FabricObject[]) {
    const updatedObjects: FabricObject[] = [];
    for (const yObject of fabricObjectsUpdates) {
      updatedObjects.push(
        this.normalizeObjectCoordinatesIfNeeded(this.prepareObjectForSendingUpdate(yObject)),
      );
    }

    this.ySessionBoardItemsAdapter.modifyInYSession(this.spaceId, updatedObjects);
  }

  private sendObjectsRemovalUpdates(objectToRemove: FabricObject[]) {
    this.ySessionBoardItemsAdapter.removeFromYSession(this.spaceId, objectToRemove);
  }

  public sendObjectsAdditionUpdates(fabricObjects: FabricObject[]): void {
    const addedObjects: FabricObject[] = [];
    for (const fabricObject of fabricObjects) {
      // not setting the scribble type in the case of object copy in necessary as getScribbleType depends on the type of tool currently selected
      if (!fabricObject.ScribbleType) {
        fabricObject.ScribbleType = this.getScribbleType();
      }

      // only set uid and ScribbleType if not already set (in case of new objects added, not copied) original PR #3334
      if (!fabricObject.uid) {
        fabricObject.uid = this.sessionSyncService.getFabricScribbleUid(fabricObject.ScribbleType);
      }

      // Set the userId to identify who created this object for remote users
      // BoardItems handle userId in their toObject/toFrameItem methods via configurations.ownerId
      if (!fabricObject.userId) {
        fabricObject.userId = this.userService.userId;
      }

      fabricObject.vuid = uuidv4();

      // For objects added in patches (copied and pasted objects), the position needs a special handling as there is a group transformation applied
      addedObjects.push(
        this.normalizeObjectCoordinatesIfNeeded(getFabricObjectWithAbsolutePosition(fabricObject)),
      );
    }

    this.ySessionBoardItemsAdapter.addToYSession(this.spaceId, addedObjects);
  }

  private getScribbleType(): ScribbleType {
    if (isScreenShareAnnotationsSpaceID(this.spaceId)) {
      return this.screenShareAnnotationsManagerService.getScribbleType();
    }
    return this.mainToolbarService.getScribbleType();
  }

  public sendObjectAdditionUpdate(fabricObject: FabricObject) {
    return this.sendObjectsAdditionUpdates([fabricObject]);
  }

  public insertBoardItem(boardItem: BoardItem, toCanvasOnly = false) {
    if (toCanvasOnly) {
      boardItem.skipRemoteAddition = true;
    }

    if (this.spaceBoardsService.activeSpaceSelectedBoard?.uid === boardItem.boardUid) {
      // when adding to the canvas, the canvas will take care of syncing it with the service
      this.sharedDataService.fabricCanvas?.add(boardItem);
    } else if (!toCanvasOnly) {
      // if we are not adding it to the canvas we need to sync it with the server ourselves
      this.sendObjectAdditionUpdate(boardItem);
    }
  }

  public insertNewBoardItem<T extends keyof ItemModelToConfigurationsMap>(
    itemModel: T,
    boardItemConfiguration: ItemModelToConfigurationsMap[T],
    providedCreationContext?: Partial<CreationContext>,
    toCanvasOnly = false,
  ): ItemModelToBoardItemsMap[T] {
    const boardItem = this.boardItemsFactory.createBoardItem<typeof itemModel>(
      itemModel,
      boardItemConfiguration,
      { ...(providedCreationContext ?? {}), isNewlyCreatedItem: true },
    );

    this.insertBoardItem(boardItem, toCanvasOnly);

    return boardItem as ItemModelToBoardItemsMap[T];
  }

  // This should be called only for board items that was inserted to the board but not yet synced with other users
  public setOfflineBoardItemStateAndSyncItWithTheServer<
    T extends keyof ItemModelToConfigurationsMap,
  >(boardItem: BoardItem, config: ItemModelToConfigurationsMap[T]) {
    boardItem.configurations = defaults(boardItem.configurations, config);

    // because it was out of sync until the config was added to it
    boardItem.skipRemoteAddition = false;

    // For adding the object to the yDoc now that it has the correct state
    this.canvas.fire('object:added', { target: boardItem });
    // For updating the local state of the previously offline item
    this.canvas.stateManager.updateLocalState(boardItem);
  }

  private prepareObjectForSendingUpdate(object: FabricObject) {
    // Changing the version to indicate a change in the state,
    //  we are assuming that the current object have the latest state, by assigning the new vuid to it
    //  so that we don't have to re-update it when the update we send gets send back to us
    object.vuid = uuidv4();

    // The position needs a special handling when the object is in a group selection
    return getFabricObjectWithAbsolutePosition(object);
  }

  private normalizeObjectCoordinatesIfNeeded(object: FabricObject): FabricObject {
    if (!isScreenShareAnnotationsSpaceID(this.spaceId)) {
      return object;
    }

    const dimensions = {
      width: this.canvas.getWidth(),
      height: this.canvas.getHeight(),
    };
    if (!isValidCanvasDimensions(dimensions)) {
      return object;
    }

    normalizeObjectCoordinates(object, dimensions);
    return object;
  }

  // Handling group selection and sticky notes cases
  private getObjectsFromModificationEvent(event: fabric.IEvent<Event>): FabricObject[] {
    const target: any = event.target;

    if (!target) {
      return [];
    }

    let objects: fabric.Object[];
    if (isBoardItem(target)) {
      objects = [target];
    } else {
      objects = target.objects ?? target._objects ?? [target];
    }

    return objects.filter((object) => !object.excludeFromExport).flat() as FabricObject[];
  }

  // Setting the boardUid to the item if not set
  private getObjectFromEvent(event: fabric.IEvent<Event>): FabricObject {
    const object = event.target as FabricObject;
    if (!object?.boardUid) {
      // for objects that are not of type BoardItemComponent,
      // We are handling the case that if the user switched board instantly after doing the action, so the action still registered to be done on the correct board
      object.boardUid = this.boardId;
    }

    return object;
  }

  wrapObservableWithBufferIfNeeded(
    observable: Observable<FabricObject>,
  ): Observable<FabricObject[]> {
    if (this.flagsService.isFlagEnabled(FLAGS.BUFFER_FABRIC_UPDATE_ON_APPLY_UPDATE_LEVEL)) {
      return this.observerUtils.bufferedObservableOf(
        observable,
        this.sessionSyncService.getSocketEventProcessRate(),
      );
    }
    return observable.pipe(map((o) => [o]));
  }

  wrapArrayObservableWithBufferIfNeeded(
    observable: Observable<FabricObject[]>,
  ): Observable<FabricObject[]> {
    if (this.flagsService.isFlagEnabled(FLAGS.BUFFER_FABRIC_UPDATE_ON_APPLY_UPDATE_LEVEL)) {
      return this.observerUtils
        .bufferedObservableOf(observable, this.sessionSyncService.getSocketEventProcessRate())
        .pipe(map((objects) => flatten<FabricObject>(objects)));
    }
    return observable;
  }

  public destroyService() {
    this.currentBoardSubscription.unsubscribe();
    this.remoteUpdateToCanvasSubscription.unsubscribe();
    this.selectionCreatedSubscription.unsubscribe();
    this.selectionUpdatedSubscription.unsubscribe();
    this.objectModifiedSubscription.unsubscribe();
    this.objectAddedSubscription.unsubscribe();
    this.objectRemovedSubscription.unsubscribe();
    this.inBoundUpdatesService?.destroyService();
  }
}
